import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getErrorMessage } from '@/lib/error-utils';

interface Tenant {
  id: string;
  business_name: string;
  status: 'active' | 'suspended' | 'inactive' | 'pending';
  schema_name: string;
  created_at: string;
}

interface TenantSelectionContextType {
  selectedTenant: Tenant | null;
  availableTenants: Tenant[];
  loading: boolean;
  error: string | null;
  selectTenant: (tenant: Tenant | null) => void;
  refreshTenants: () => Promise<void>;
}


const TenantSelectionContext = createContext<TenantSelectionContextType | undefined>(undefined);

// The stored selection is stamped with the auth user it was made for. Without
// that stamp a selection made under one identity survives a sign-out/sign-in in
// the same tab and every later screen queries the wrong tenant: the RPCs check
// the caller's own tenant, deny the mismatched id, and the screen renders its
// empty state as if the data were gone. Measured 2026-09-02 on a tab where the
// platform-admin session was still answering when this context ran once.
const SELECTED_TENANT_KEY = 'selectedTenantId';
const SELECTED_TENANT_OWNER_KEY = 'selectedTenantOwner';

function readSavedTenantId(userId: string): string | null {
  if (localStorage.getItem(SELECTED_TENANT_OWNER_KEY) !== userId) return null;
  return localStorage.getItem(SELECTED_TENANT_KEY);
}

function saveSelectedTenant(tenantId: string, userId: string | null) {
  localStorage.setItem(SELECTED_TENANT_KEY, tenantId);
  if (userId) localStorage.setItem(SELECTED_TENANT_OWNER_KEY, userId);
}

function clearSelectedTenant() {
  localStorage.removeItem(SELECTED_TENANT_KEY);
  localStorage.removeItem(SELECTED_TENANT_OWNER_KEY);
}

export function TenantSelectionProvider({ children }: { children: React.ReactNode }) {
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [availableTenants, setAvailableTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // undefined = identity not resolved yet; null = signed out.
  const authUserIdRef = useRef<string | null | undefined>(undefined);

  const fetchTenants = async () => {
    try {
      console.log('TenantSelectionContext: Starting tenant fetch');
      setLoading(true);
      setError(null);

      // Check if user is authenticated first
      // Read from the stored session: `getUser()` is a network round trip to the auth server and the
      // next call below is rejected by the database anyway when the session is not valid.
      const { data: { session: storedSession } } = await supabase.auth.getSession();
      const user = storedSession?.user ?? null;
      if (!user) {
        console.log('TenantSelectionContext: No authenticated user, skipping tenant fetch');
        setAvailableTenants([]);
        setLoading(false);
        return;
      }
      
      console.log('TenantSelectionContext: Found authenticated user:', user.email);

      // Session is already established - no need to set it again

      // Check if user is platform admin first
      let isPlatformAdmin = false;
      try {
        const { data } = await supabase.rpc('is_platform_admin_safe');
        isPlatformAdmin = !!data;
        console.log('TenantSelectionContext: isPlatformAdmin =', isPlatformAdmin);
      } catch (err) {
        console.warn('Could not check platform admin status:', err);
      }

      // For regular users, get their tenant directly using RPC function
      if (!isPlatformAdmin) {
        console.log('DEBUG TenantSelectionContext: Getting tenant for regular user');
        try {
          // Use the proper RPC function to get tenant ID
          console.log('DEBUG TenantSelectionContext: Calling get_current_user_tenant_id RPC');
          const { data: tenantId, error: rpcError } = await supabase.rpc('get_current_user_tenant_id');

          console.log('DEBUG TenantSelectionContext: RPC result:', { tenantId, rpcError });

          if (rpcError) throw rpcError;

          if (tenantId) {
            console.log('DEBUG TenantSelectionContext: User tenant_id:', tenantId);

            // Get the specific tenant for this user
            const { data: tenantData, error: tenantError } = await supabase
              .from('tenants')
              .select('id, business_name, status, schema_name, created_at')
              .eq('id', tenantId)
              .eq('status', 'active')
              .single();

            if (tenantError || !tenantData) {
              console.warn('TenantSelectionContext: Tenant not found or inactive for id:', tenantId, '— clearing stale reference');
              clearSelectedTenant();
              setSelectedTenant(null);
              setAvailableTenants([]);
              setLoading(false);
              return;
            }

            console.log('DEBUG TenantSelectionContext: Found user tenant:', tenantData.business_name);
            setAvailableTenants([tenantData]);
            setSelectedTenant(tenantData);
            saveSelectedTenant(tenantData.id, user.id);
            return;
          }

          // Regular user with no tenant — needs onboarding, don't fall through to admin query
          console.log('TenantSelectionContext: Regular user has no tenant, needs onboarding');
          setAvailableTenants([]);
          setSelectedTenant(null);
          clearSelectedTenant();
          setLoading(false);
          return;
        } catch (err) {
          console.error('TenantSelectionContext: Error getting user tenant:', err);
          // Fall through to platform admin logic as fallback
        }
      }

      // Platform admin logic - get all tenants
      const { data, error } = await supabase
        .from('tenants')
        .select('id, business_name, status, schema_name, created_at')
        .eq('status', 'active')
        .order('business_name');

      if (error) throw error;

      const availableTenantsData = data || [];
      console.log('TenantSelectionContext: Available tenants:', availableTenantsData.length);

      setAvailableTenants(availableTenantsData);

      // Restore selected tenant from localStorage
      const savedTenantId = readSavedTenantId(user.id);
      if (savedTenantId && availableTenantsData.length > 0) {
        const tenant = availableTenantsData.find(t => t.id === savedTenantId);
        if (tenant) {
          console.log('TenantSelectionContext: Restored saved tenant:', tenant.business_name);
          setSelectedTenant(tenant);
        } else {
          clearSelectedTenant();
          // Auto-select default tenant
          const defaultTenant = isPlatformAdmin 
            ? availableTenantsData.find(t => t.id === '00000000-0000-0000-0000-000000000001') 
            : availableTenantsData.find(t => t.id !== '00000000-0000-0000-0000-000000000001');
          if (defaultTenant) {
            console.log('TenantSelectionContext: Auto-selected default tenant:', defaultTenant.business_name);
            setSelectedTenant(defaultTenant);
            saveSelectedTenant(defaultTenant.id, user.id);
          }
        }
      } else if (availableTenantsData.length > 0) {
        // Auto-select default tenant if no saved selection
        const defaultTenant = isPlatformAdmin 
          ? availableTenantsData.find(t => t.id === '00000000-0000-0000-0000-000000000001') 
          : availableTenantsData.find(t => t.id !== '00000000-0000-0000-0000-000000000001');
        if (defaultTenant) {
          console.log('TenantSelectionContext: Auto-selected default tenant (no saved):', defaultTenant.business_name);
          setSelectedTenant(defaultTenant);
          saveSelectedTenant(defaultTenant.id, user.id);
        }
      }
    } catch (err: unknown) {
      console.error('TenantSelectionContext: Error fetching tenants:', err);
      setError(getErrorMessage(err));
      // Don't keep loading forever on error
      setAvailableTenants([]);
    } finally {
      setLoading(false);
    }
  };

  const selectTenant = (tenant: Tenant | null) => {
    setSelectedTenant(tenant);
    if (tenant) {
      saveSelectedTenant(tenant.id, authUserIdRef.current ?? null);
    } else {
      clearSelectedTenant();
    }
  };

  const refreshTenants = async () => {
    await fetchTenants();
  };

  // This used to be a single fire-and-forget fetch on a 500ms timer, which meant
  // the context resolved the tenant once, against whichever identity happened to
  // be answering at that moment, and never looked again. Following the auth
  // identity instead removes both the guess and the staleness.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    const onIdentity = (userId: string | null) => {
      // Only an actual change of user matters here. Token refreshes re-emit the
      // same subject and re-fetching on those is what the auth event storm fix
      // (2360f963) was about.
      if (authUserIdRef.current === userId) return;
      const hadIdentity = authUserIdRef.current !== undefined;
      authUserIdRef.current = userId;

      if (hadIdentity) {
        // A different user is answering now: nothing chosen for the previous one
        // may survive into this session.
        clearSelectedTenant();
        setSelectedTenant(null);
        setAvailableTenants([]);
      }

      if (!userId) {
        setLoading(false);
        return;
      }

      // Out of the auth callback before touching supabase again: calling into
      // the client from inside onAuthStateChange can deadlock it.
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => { fetchTenants(); }, 0);
    };

    supabase.auth.getSession().then(({ data }) => onIdentity(data.session?.user?.id ?? null));

    const { data: subscription } = supabase.auth.onAuthStateChange((_event, session) => {
      onIdentity(session?.user?.id ?? null);
    });

    return () => {
      if (timer) clearTimeout(timer);
      subscription.subscription.unsubscribe();
    };
  }, []);

  const value = {
    selectedTenant,
    availableTenants,
    loading,
    error,
    selectTenant,
    refreshTenants
  };

  return (
    <TenantSelectionContext.Provider value={value}>
      {children}
    </TenantSelectionContext.Provider>
  );
}

export function useTenantSelection() {
  const context = useContext(TenantSelectionContext);
  if (context === undefined) {
    throw new Error('useTenantSelection must be used within a TenantSelectionProvider');
  }
  return context;
}